import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';
import LoginModal from '../LoginModal';
import RegisterModal from '../RegisterModal';
import VerifyEmailModal from '../VerifyEmailModal';
import RequirementModal from '../RequirementModal';
import CategorySidebar from '../CategorySidebar';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';

// Helper to get proper image URL
const getImageUrl = (url) => {
  if (!url) return '';
  // Fix localhost URLs from old data
  if (url.startsWith('http://localhost')) {
    try { return `${SOCKET_URL}${new URL(url).pathname}`; } catch { return url; }
  }
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const Header = ({ isHomePage = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCurrency, currencies, changeCurrency } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isRFQModalOpen, setIsRFQModalOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState({ 
    code: i18n.language || 'en', 
    name: i18n.language === 'ar' ? 'العربية' : 'English', 
    flag: i18n.language === 'ar' ? 'sa' : 'us' 
  });

  // Mobile menu states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);
  const [showMobileLanguage, setShowMobileLanguage] = useState(false);
  const [showMobileCurrency, setShowMobileCurrency] = useState(false);
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] = useState(false);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowMobileSearch(false);
    setShowCategoryDropdown(false);
  }, [location.pathname]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCategoryDropdown && !e.target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
        setHoveredCategory(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCategoryDropdown]);

  // Close currency and language dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCurrencyDropdown && !e.target.closest('.currency-selector')) {
        setShowCurrencyDropdown(false);
      }
      if (showLanguageDropdown && !e.target.closest('.language-selector')) {
        setShowLanguageDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCurrencyDropdown, showLanguageDropdown]);

  // Close mobile menu on navigation
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setShowMobileSearch(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setShowMobileSearch(false);
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    setIsMobileMenuOpen(false);
  };

  const handleMobileCategoryClick = (category) => {
    if (expandedMobileCategory?.id === category.id) {
      setExpandedMobileCategory(null);
    } else {
      setExpandedMobileCategory(category);
    }
  };

  const handleMobileCategoryNavigate = (category) => {
    setIsMobileMenuOpen(false);
    setExpandedMobileCategory(null);
    navigate(`/products?category=${category.id}`);
  };

  const handleMobileSubcategoryClick = (category, subcategory) => {
    setIsMobileMenuOpen(false);
    setExpandedMobileCategory(null);
    navigate(`/products?category=${subcategory.id || category.id}`);
  };

  const handleMobileNavClick = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  // Handle currency selection
  const handleCurrencySelect = (currencyCode) => {
    changeCurrency(currencyCode);
    setShowCurrencyDropdown(false);
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to hardcoded categories if API fails
        setCategories([
          { id: 1, name: 'Building Materials', subcategories: [{ name: 'Cement' }, { name: 'Steel' }, { name: 'Bricks' }, { name: 'Sand' }] },
          { id: 2, name: 'Structural Work', subcategories: [{ name: 'Beams' }, { name: 'Columns' }, { name: 'Foundations' }] },
          { id: 3, name: 'Interior & Finishing', subcategories: [{ name: 'Paints' }, { name: 'Tiles' }, { name: 'Flooring' }] },
          { id: 4, name: 'Plumbing & Sanitary', subcategories: [{ name: 'Pipes' }, { name: 'Fittings' }, { name: 'Sanitary Ware' }] },
          { id: 5, name: 'Electrical Works', subcategories: [{ name: 'Wiring' }, { name: 'Switches' }, { name: 'Lighting' }] },
          { id: 6, name: 'Windows & Doors', subcategories: [{ name: 'Aluminum' }, { name: 'Wood' }, { name: 'Glass' }] },
          { id: 7, name: 'Green Building Materials', subcategories: [{ name: 'Solar Panels' }, { name: 'Insulation' }] },
          { id: 8, name: 'Scaffolding & Shuttering', subcategories: [{ name: 'Steel Scaffolding' }, { name: 'Aluminum' }] },
        ]);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (category) => {
    setShowCategoryDropdown(false);
    setHoveredCategory(null);
    navigate(`/products?category=${category.id}`);
  };

  const handleSubcategoryClick = (category, subcategory) => {
    setShowCategoryDropdown(false);
    setHoveredCategory(null);
    navigate(`/products?category=${subcategory.id || category.id}`);
  };

  const languages = [
    { code: 'en', name: 'English', flag: 'us' },
    { code: 'ar', name: 'العربية', flag: 'sa' }
  ];

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setShowLanguageDropdown(false);
    i18n.changeLanguage(language.code);
    localStorage.setItem('language', language.code);
    // Set document direction for RTL languages
    document.documentElement.dir = language.code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language.code;
  };

  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  // Login/Register Modal handlers
  const openLoginModal = () => {
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const openRegisterModal = () => {
    setIsLoginModalOpen(false);
    setIsVerifyEmailModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const closeModals = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(false);
  };

  const handleVerifyEmail = (email) => {
    setRegisteredEmail(email);
    setIsRegisterModalOpen(false);
    setIsVerifyEmailModalOpen(true);
  };

  const handleVerifyEmailContinue = () => {
    setIsVerifyEmailModalOpen(false);
    // User is already logged in after registration
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  // Search functionality
  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const openRFQModal = () => {
    if (!isAuthenticated) {
      setLoginRedirectPath('/send-inquire');
      setIsLoginModalOpen(true);
      return;
    }
    setIsRFQModalOpen(true);
  };

  const closeRFQModal = () => {
    setIsRFQModalOpen(false);
  };

  return (
    <header className="header">
      {/* ========== MOBILE HEADER ========== */}
      <div className="mobile-header">
        {/* Mobile Top Bar */}
        <div className="mobile-header-top">
          <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Menu">
            {isMobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#1A1A1A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="hamburger-icon-img" />
            )}
          </button>

          <NavLink to="/" className="mobile-logo">
            <img src="https://api.builder.io/api/v1/image/assets/TEMP/befb5eeb3b9965ba933167c79645224a11281e16?width=550" alt="Gulf Constructor" />
          </NavLink>

          <div className="mobile-header-actions">
            <div className="mobile-profile-wrapper">
              <button 
                className="mobile-login-btn" 
                onClick={() => setShowMobileProfileDropdown(!showMobileProfileDropdown)} 
                aria-label="Profile"
              >
                {isAuthenticated && (user?.companyLogo || user?.avatar) ? (
                  <div className="mobile-user-avatar">
                    <img src={getImageUrl(user.companyLogo || user.avatar)} alt="User" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ) : (
                  <img src="/mobile-icons/right-sidebar.png" alt="Profile" className="mobile-profile-icon-img" />
                )}
              </button>
              
              {/* Mobile Profile Dropdown */}
              {showMobileProfileDropdown && (
                <>
                  <div className="mobile-profile-overlay" onClick={() => setShowMobileProfileDropdown(false)}></div>
                  <div className="mobile-profile-dropdown">
                    {!isAuthenticated && (
                      <div className="mobile-profile-auth">
                        <button className="mobile-profile-signin" onClick={() => { setShowMobileProfileDropdown(false); openLoginModal(); }}>Sign In</button>
                        <button className="mobile-profile-joinfree" onClick={() => { setShowMobileProfileDropdown(false); openRegisterModal(); }}>Join Free</button>
                      </div>
                    )}
                    
                    <div className="mobile-profile-menu">
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          navigate(isAdmin ? '/admin/messages' : '/messages');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M22 6L12 13L2 6" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Inquiries</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          navigate(isAdmin ? '/admin/messages' : '/messages');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Chats</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          navigate('/rfq');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 13H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 17H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 9H9H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Quotations</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          navigate('/favorites');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Favorites</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          navigate('/following');
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Follow</span>
                      </button>
                      
                      <button className="mobile-profile-item" onClick={() => { 
                        setShowMobileProfileDropdown(false); 
                        if (isAuthenticated) {
                          setIsRFQModalOpen(true);
                        } else {
                          openLoginModal();
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Post Sourcing Request</span>
                      </button>
                      
                      {isAuthenticated && (
                        <button className="mobile-profile-logout" onClick={() => { setShowMobileProfileDropdown(false); handleLogout(); }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 12H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Logout
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Search Bar - Always Visible */}
        <div className="mobile-search-container">
          <div className="mobile-search-wrapper">
            <input
              type="text"
              placeholder={t('header.searchPlaceholder') || 'Enter your keywords.....'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="mobile-search-input"
            />
            <button className="mobile-search-submit" onClick={handleSearch}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}></div>
        <nav className={`mobile-nav-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
          {/* Mobile Categories Section - First */}
          <div className="mobile-sidebar-section">
            <div className="mobile-sidebar-header">
              <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="sidebar-hamburger-icon" />
              <span>Categories</span>
            </div>
            <div className="mobile-sidebar-list">
              {categories.slice(0, 7).map((category) => (
                <div 
                  key={category.id || category.name} 
                  className="mobile-sidebar-item"
                  onClick={() => handleMobileCategoryNavigate(category)}
                >
                  {category.name}
                </div>
              ))}
              <NavLink 
                to="/all-categories" 
                className="mobile-sidebar-item mobile-sidebar-viewmore" 
                onClick={() => handleMobileNavClick('/all-categories')}
              >
                View More
              </NavLink>
            </div>
          </div>

          {/* Divider */}
          <div className="mobile-sidebar-divider"></div>

          {/* Quick Links Section */}
          <div className="mobile-sidebar-section">
            <div 
              className="mobile-sidebar-link"
              onClick={() => { setIsMobileMenuOpen(false); openRFQModal(); }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#333" strokeWidth="2"/>
                <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#333" strokeWidth="2"/>
              </svg>
              <span>Post Sourcing Request</span>
            </div>
            <div 
              className="mobile-sidebar-link"
              onClick={() => handleMobileNavClick('/get-app')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="5" y="2" width="14" height="20" rx="2" stroke="#333" strokeWidth="2"/>
                <path d="M12 18H12.01" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Get App</span>
            </div>
            <div 
              className="mobile-sidebar-link mobile-sidebar-link-arrow"
              onClick={() => setShowMobileLanguage(!showMobileLanguage)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#333" strokeWidth="2"/>
                <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#333" strokeWidth="2"/>
              </svg>
              <span>{selectedLanguage.name}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mobile-sidebar-arrow">
                <path d="M9 18L15 12L9 6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            {showMobileLanguage && (
              <div className="mobile-sidebar-suboptions">
                {languages.map((lang) => (
                  <div 
                    key={lang.code}
                    className={`mobile-sidebar-suboption ${selectedLanguage.code === lang.code ? 'active' : ''}`}
                    onClick={() => { handleLanguageSelect(lang); setShowMobileLanguage(false); }}
                  >
                    <img src={`https://flagcdn.com/w80/${lang.flag}.png`} alt={lang.name} className="mobile-sidebar-flag" />
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div 
              className="mobile-sidebar-link"
              onClick={() => handleMobileNavClick('/contact')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6408 21.8227C20.377 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77383 17.3147 6.72534 15.2662 5.19001 12.85C3.49998 10.2412 2.44824 7.27099 2.12001 4.18C2.09501 3.90347 2.12789 3.62476 2.21649 3.36162C2.30508 3.09849 2.44756 2.85669 2.63477 2.65162C2.82197 2.44655 3.04981 2.28271 3.30379 2.17052C3.55778 2.05833 3.83234 2.00026 4.11001 2H7.11001C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04208 3.23945 9.11001 3.72C9.23662 4.68007 9.47145 5.62273 9.81001 6.53C9.94455 6.88792 9.97366 7.27691 9.89391 7.65088C9.81415 8.02485 9.62886 8.36811 9.36001 8.64L8.09001 9.91C9.51356 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9752 14.1859 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Contact Us</span>
            </div>
          </div>

          {/* Divider */}
          <div className="mobile-sidebar-divider"></div>

          {/* Footer Links */}
          <div className="mobile-sidebar-section mobile-sidebar-footer">
            <NavLink 
              to="/user-agreement" 
              className="mobile-sidebar-footer-link"
              onClick={() => handleMobileNavClick('/user-agreement')}
            >
              User Agreement
            </NavLink>
            <NavLink 
              to="/privacy-policy" 
              className="mobile-sidebar-footer-link"
              onClick={() => handleMobileNavClick('/privacy-policy')}
            >
              Privacy Policy
            </NavLink>
          </div>
        </nav>
      </div>

      {/* ========== DESKTOP HEADER ========== */}
      {/* Top Navbar */}
      <div className="header-top desktop-only">
        {/* Top Row - Currency and Language */}
        <div className="header-top-row">
          <div className="currency-selector" onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}>
            <span>{selectedCurrency.code} {selectedCurrency.symbol}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#1F396C" stroke="#1F396C" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            {showCurrencyDropdown && (
              <div className="currency-dropdown">
                {currencies.map((curr) => (
                  <div 
                    key={curr.code} 
                    className={`currency-option ${selectedCurrency.code === curr.code ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleCurrencySelect(curr.code); }}
                  >
                    <span className="currency-code">{curr.code}</span>
                    <span className="currency-symbol">{curr.symbol}</span>
                    <span className="currency-name">{curr.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="language-selector" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
            <div className="flag-container">
              <img src={`https://flagcdn.com/w80/${selectedLanguage.flag}.png`} alt="Flag" className="flag-icon" />
            </div>
            <div className="language-text-wrapper">
              <span>{selectedLanguage.name}</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#1F396C"/>
              </svg>
            </div>
            {showLanguageDropdown && (
              <div className="language-dropdown">
                {languages.map((lang) => (
                  <div 
                    key={lang.code} 
                    className="language-option"
                    onClick={(e) => { e.stopPropagation(); handleLanguageSelect(lang); }}
                  >
                    <img src={`https://flagcdn.com/w80/${lang.flag}.png`} alt={lang.name} className="dropdown-flag" />
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Main Row - Logo, Search, Actions */}
        <div className="header-main-row">
          <div className="logo-container">
            <NavLink to="/">
              <img src="https://api.builder.io/api/v1/image/assets/TEMP/befb5eeb3b9965ba933167c79645224a11281e16?width=550" alt="Gulf Constructor" className="header-logo" />
            </NavLink>
          </div>
          
          <div className="search-container">
            <div className="search-input-wrapper">
              <input 
                type="text" 
                placeholder={t('header.searchPlaceholder')} 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="search-input"
              />
            </div>
            <button className="search-button" onClick={handleSearch}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M22.5 22L15.8333 15.3333M2.5 9.77778C2.5 10.7992 2.70118 11.8106 3.09205 12.7542C3.48292 13.6978 4.05582 14.5553 4.77806 15.2775C5.50029 15.9997 6.35771 16.5726 7.30135 16.9635C8.24499 17.3544 9.25639 17.5556 10.2778 17.5556C11.2992 17.5556 12.3106 17.3544 13.2542 16.9635C14.1978 16.5726 15.0553 15.9997 15.7775 15.2775C16.4997 14.5553 17.0726 13.6978 17.4635 12.7542C17.8544 11.8106 18.0556 10.7992 18.0556 9.77778C18.0556 8.75639 17.8544 7.74499 17.4635 6.80135C17.0726 5.85771 16.4997 5.00029 15.7775 4.27806C15.0553 3.55582 14.1978 2.98292 13.2542 2.59205C12.3106 2.20118 11.2992 2 10.2778 2C9.25639 2 8.24499 2.20118 7.30135 2.59205C6.35771 2.98292 5.50029 3.55582 4.77806 4.27806C4.05582 5.00029 3.48292 5.85771 3.09205 6.80135C2.70118 7.74499 2.5 8.75639 2.5 9.77778Z" stroke="white" strokeOpacity="0.8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          
          <div className="header-actions">
            <div className="action-item" onClick={openRegisterModal} style={{ cursor: 'pointer' }}>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M33.1337 10.6006H33.1333L30.8328 4.41835C30.6767 4.00273 30.3978 3.64444 30.0333 3.3911C29.6687 3.13776 29.2356 3.00135 28.7916 3H7.57779C7.1338 3.00135 6.70074 3.13777 6.33614 3.39114C5.97155 3.64451 5.69268 4.00283 5.53661 4.41848L3.23611 10.6008C2.95025 11.3708 2.92302 12.188 3.15954 12.9019C3.3682 13.5324 3.71858 14.1066 4.18376 14.5806C4.64895 15.0545 5.21657 15.4155 5.84302 15.6359V28.4097C5.84423 29.372 6.22705 30.2946 6.90752 30.9751C7.58799 31.6556 8.51057 32.0384 9.47291 32.0396H26.8968C27.8591 32.0384 28.7817 31.6556 29.4622 30.9751C30.1427 30.2946 30.5255 29.372 30.5267 28.4097V15.6357C31.1532 15.4154 31.7208 15.0544 32.186 14.5804C32.6512 14.1065 33.0016 13.5323 33.2102 12.9018C33.4471 12.1878 33.4198 11.3706 33.1337 10.6006ZM24.6415 9.8853C24.6929 10.1767 24.7188 10.4721 24.7188 10.768V11.5303C24.7207 11.9128 24.6469 12.292 24.5018 12.6459C24.3567 12.9999 24.1431 13.3217 23.8732 13.5928C23.6034 13.864 23.2826 14.0791 22.9294 14.226C22.5762 14.3728 22.1974 14.4484 21.8148 14.4484C21.4323 14.4484 21.0535 14.3728 20.7003 14.226C20.347 14.0791 20.0263 13.864 19.7564 13.5928C19.4866 13.3217 19.273 12.9999 19.1279 12.6459C18.9828 12.292 18.909 11.9128 18.9109 11.5303V4.45183H23.6826L24.6415 9.8853ZM11.6509 10.768C11.6508 10.472 11.6767 10.1765 11.7281 9.88496L12.6871 4.45183H17.4589V11.5303C17.4589 11.9116 17.3838 12.2893 17.2378 12.6416C17.0919 12.9939 16.878 13.3141 16.6083 13.5837C16.3387 13.8534 16.0185 14.0673 15.6662 14.2132C15.3139 14.3592 14.9363 14.4343 14.5549 14.4343C14.1735 14.4343 13.7959 14.3592 13.4436 14.2132C13.0913 14.0673 12.7711 13.8534 12.5015 13.5837C12.2318 13.3141 12.0179 12.9939 11.8719 12.6416C11.726 12.2893 11.6509 11.9116 11.6509 11.5303V10.768ZM4.53773 12.4449C4.40446 12.0433 4.4257 11.5681 4.59694 11.1067L6.89744 4.9248C6.9495 4.78622 7.04249 4.66676 7.16406 4.58229C7.28563 4.49783 7.43003 4.45235 7.57806 4.4519H11.2126L10.2982 9.63292C10.2321 10.0078 10.1989 10.3877 10.1989 10.7683V11.5303C10.1988 12.2203 9.95312 12.8877 9.5058 13.413C9.05847 13.9383 8.43876 14.2872 7.75763 14.3972C7.07649 14.5073 6.37843 14.3713 5.78845 14.0135C5.19846 13.6558 4.75509 13.0997 4.53773 12.4449ZM29.0748 28.4097C29.0742 28.9871 28.8445 29.5407 28.4362 29.9491C28.0279 30.3574 27.4742 30.587 26.8968 30.5877H9.47291C8.89546 30.587 8.34185 30.3574 7.93354 29.9491C7.52522 29.5407 7.29555 28.9871 7.29492 28.4097V15.8863C8.01395 15.886 8.72173 15.7078 9.35511 15.3675C9.9885 15.0271 10.5278 14.5353 10.9249 13.9359C11.3218 14.5355 11.861 15.0275 12.4945 15.3678C13.1279 15.7081 13.8358 15.8863 14.5549 15.8863C15.2739 15.8863 15.9818 15.7081 16.6153 15.3678C17.2487 15.0275 17.7879 14.5355 18.1849 13.9359C18.5818 14.5355 19.121 15.0275 19.7544 15.3678C20.3879 15.7081 21.0958 15.8863 21.8148 15.8863C22.5339 15.8863 23.2418 15.7081 23.8752 15.3678C24.5087 15.0275 25.0479 14.5355 25.4448 13.9359C25.8419 14.5353 26.3812 15.0271 27.0146 15.3675C27.648 15.7078 28.3558 15.886 29.0748 15.8863V28.4097ZM31.832 12.4449C31.6146 13.0997 31.1712 13.6558 30.5813 14.0135C29.9913 14.3713 29.2932 14.5073 28.6121 14.3972C27.9309 14.2872 27.3112 13.9383 26.8639 13.413C26.4166 12.8877 26.1709 12.2203 26.1708 11.5303V10.768C26.1708 10.3874 26.1376 10.0074 26.0715 9.63258L25.1571 4.45183H28.7916C28.9396 4.45227 29.084 4.49773 29.2056 4.58215C29.3272 4.66658 29.4202 4.78599 29.4723 4.92452L31.7722 11.1065C31.944 11.5681 31.9653 12.0432 31.832 12.4449Z" fill="#333333"/>
              </svg>
              <span>{t('header.becomeSupplier').split(' ').slice(0, 2).join(' ')}<br/>{t('header.becomeSupplier').split(' ').slice(2).join(' ') || ''}</span>
            </div>
            <div className="action-item" onClick={openRFQModal} style={{ cursor: 'pointer' }}>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M20.2429 9.83397C19.5161 9.63475 18.7614 9.53373 18 9.53373C13.3317 9.53373 9.53373 13.3317 9.53373 18C9.53373 22.6683 13.3317 26.4663 18 26.4663C22.6683 26.4663 26.4663 22.6683 26.4663 18C26.4663 17.2386 26.3653 16.4839 26.166 15.7571C26.0292 15.2578 26.323 14.742 26.8223 14.6051C27.3214 14.4683 27.8374 14.7621 27.9743 15.2614C28.2183 16.1538 28.3417 17.0749 28.3413 18C28.3413 23.7022 23.7022 28.3413 18 28.3413C12.2978 28.3413 7.65873 23.7022 7.65873 18C7.65873 12.2978 12.2978 7.65873 18 7.65873C18.9289 7.65873 19.8502 7.78219 20.7386 8.0257C21.155 8.13984 21.4285 8.51754 21.4284 8.92945C21.4284 9.01332 21.4171 9.09679 21.3949 9.17766C21.258 9.67699 20.7424 9.97102 20.2429 9.83397ZM31.2198 13.1039C30.7233 13.2509 30.44 13.7725 30.587 14.269C30.944 15.4748 31.125 16.7301 31.125 18C31.125 25.2372 25.2372 31.125 18 31.125C10.7629 31.125 4.875 25.2372 4.875 18C4.875 10.7629 10.7629 4.875 18 4.875C19.27 4.875 20.5252 5.056 21.7308 5.41295C22.2274 5.56002 22.7489 5.27654 22.8959 4.78014C22.9222 4.69137 22.9347 4.60189 22.9347 4.51383C22.9348 4.10924 22.6709 3.73582 22.2631 3.61506C20.8845 3.20689 19.4503 3 18 3C13.9934 3 10.2265 4.56029 7.39336 7.39336C4.56029 10.2265 3 13.9934 3 18C3 22.0066 4.56029 25.7735 7.39336 28.6066C10.2264 31.4398 13.9934 33 18 33C22.0066 33 25.7735 31.4397 28.6066 28.6066C31.4397 25.7735 33 22.0066 33 18C33 16.5499 32.793 15.1155 32.3849 13.7367C32.2379 13.2403 31.7162 12.9571 31.2198 13.1039ZM18 14.25C18.1379 14.25 18.2771 14.2576 18.4138 14.2726C18.9288 14.3293 19.3916 13.9576 19.448 13.443C19.5045 12.9283 19.133 12.4653 18.6183 12.4089C18.413 12.3863 18.2066 12.375 18 12.375C14.8983 12.375 12.375 14.8983 12.375 18C12.375 21.1017 14.8983 23.625 18 23.625C21.1017 23.625 23.625 21.1017 23.625 18C23.625 17.4822 23.2053 17.0625 22.6875 17.0625C22.1697 17.0625 21.75 17.4822 21.75 18C21.75 20.0678 20.0678 21.75 18 21.75C15.9322 21.75 14.25 20.0678 14.25 18C14.25 15.9322 15.9322 14.25 18 14.25ZM22.7868 11.8876L23.2538 7.20316C23.2752 6.98771 23.3706 6.78632 23.5237 6.63322L26.8825 3.27457C27.0092 3.1479 27.1695 3.06018 27.3445 3.0218C27.5195 2.98342 27.7018 2.99598 27.8699 3.05801C28.038 3.12003 28.1848 3.22892 28.2929 3.37177C28.401 3.51463 28.466 3.68547 28.48 3.86408L28.7463 7.25373L32.1359 7.51998C32.3145 7.534 32.4854 7.59892 32.6283 7.70704C32.7711 7.81516 32.88 7.96196 32.9421 8.13005C33.0041 8.29814 33.0167 8.48049 32.9783 8.6555C32.9399 8.83051 32.8521 8.99085 32.7254 9.11754L29.3667 12.4762C29.2136 12.6293 29.0123 12.7247 28.7968 12.7461L24.1126 13.2134L18.6629 18.6629C18.576 18.7501 18.4726 18.8192 18.3589 18.8664C18.2451 18.9135 18.1231 18.9377 18 18.9375C17.8769 18.9377 17.7549 18.9135 17.6411 18.8664C17.5274 18.8192 17.4241 18.7501 17.3371 18.6629C16.971 18.2968 16.971 17.7032 17.3371 17.3371L22.7868 11.8876ZM24.7329 11.2672L28.2776 10.9137L29.9613 9.22998L27.801 9.06029C27.5785 9.04279 27.3694 8.94643 27.2116 8.78856C27.0537 8.63069 26.9573 8.42166 26.9398 8.19908L26.7701 6.03867L25.0863 7.72242L24.7329 11.2672Z" fill="#333333"/>
              </svg>
              <span>{t('header.requestQuotation').split(' ').slice(0, 2).join(' ')}<br/>{t('header.requestQuotation').split(' ').slice(2).join(' ') || ''}</span>
            </div>
            
            {/* User Authentication Section */}
            {isAuthenticated ? (
              <div 
                className="action-item user-menu-container" 
                style={{ cursor: 'pointer', position: 'relative' }}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                {/* Profile Section - Horizontal Layout */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {/* Company Logo */}
                  <div className="company-logo-wrapper" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '2px solid #1F396C',
                    flexShrink: 0
                  }}>
                    {user?.companyLogo ? (
                      <img 
                        src={getImageUrl(user.companyLogo)} 
                        alt={user?.companyName || 'Company'} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : user?.avatar ? (
                      <img 
                        src={getImageUrl(user.avatar)} 
                        alt={user?.companyName || 'User'} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 36 36" fill="none">
                        <path d="M18 3.37109C20.2173 3.37109 22.3741 4.09847 24.1377 5.44238C25.9013 6.78629 27.1745 8.67272 27.7627 10.8105C28.3507 12.9481 28.2213 15.2197 27.3936 17.2764C26.5656 19.3333 25.0849 21.0621 23.1797 22.1963C27.0314 23.4549 30.3319 26.1099 32.4736 29.8125C32.5521 29.9405 32.6044 30.083 32.627 30.2314C32.6495 30.3798 32.6423 30.5313 32.6055 30.6768C32.5686 30.8222 32.5026 30.9594 32.4121 31.0791C32.3215 31.1989 32.2072 31.2999 32.0771 31.375C31.9471 31.4501 31.8033 31.4979 31.6543 31.5166C31.5052 31.5353 31.3536 31.5242 31.209 31.4834C31.0645 31.4426 30.9298 31.373 30.8125 31.2793C30.6952 31.1856 30.598 31.0694 30.5264 30.9375C27.877 26.3587 23.1933 23.625 18 23.625C12.8069 23.6252 8.12391 26.3589 5.47461 30.9375C5.40295 31.0694 5.30574 31.1856 5.18848 31.2793C5.07121 31.373 4.93644 31.4426 4.79199 31.4834C4.64741 31.5242 4.49573 31.5353 4.34668 31.5166C4.19771 31.498 4.05385 31.4501 3.92383 31.375C3.79374 31.2999 3.67951 31.1989 3.58887 31.0791C3.49835 30.9594 3.43241 30.8222 3.39551 30.6768C3.35868 30.5313 3.35147 30.3798 3.37402 30.2314C3.3966 30.083 3.44883 29.9405 3.52734 29.8125C5.66906 26.1084 8.96957 23.4535 12.8213 22.1963C10.9161 21.0621 9.43536 19.3333 8.60742 17.2764C7.7797 15.2197 7.65023 12.9481 8.23828 10.8105C8.82645 8.6727 10.0997 6.78629 11.8633 5.44238C13.6267 4.09857 15.7829 3.37119 18 3.37109ZM19.5371 5.77637C18.0095 5.47251 16.4253 5.62857 14.9863 6.22461C13.5476 6.82068 12.3174 7.83011 11.4521 9.125C10.587 10.42 10.125 11.9426 10.125 13.5C10.1273 15.5879 10.9582 17.5901 12.4346 19.0664C13.9108 20.5425 15.9124 21.3727 18 21.375C19.5575 21.375 21.0809 20.9132 22.376 20.0479C23.6709 19.1825 24.6804 17.9526 25.2764 16.5137C25.8723 15.0748 26.0284 13.4913 25.7246 11.9639C25.4208 10.4364 24.6705 9.03293 23.5693 7.93164C22.4681 6.83038 21.0646 6.08027 19.5371 5.77637Z" fill="#1F396C"/>
                      </svg>
                    )}
                  </div>
                  
                  {/* Dropdown Arrow */}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#666"/>
                  </svg>
                </div>
                
                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div 
                    className="user-dropdown-menu"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      minWidth: '180px',
                      zIndex: 1000,
                      marginTop: '8px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <NavLink 
                      to={isAdmin ? "/admin/dashboard" : "/seller-dashboard"} 
                      style={{ display: 'block', padding: '12px 16px', color: '#333', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                      onClick={() => setShowUserMenu(false)}
                    >
                      Dashboard
                    </NavLink>
                    <NavLink 
                      to={isAdmin ? "/admin/profile" : "/supplier-profile-info"} 
                      style={{ display: 'block', padding: '12px 16px', color: '#333', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                      onClick={() => setShowUserMenu(false)}
                    >
                      Profile
                    </NavLink>
                    <NavLink 
                      to={isAdmin ? "/admin/messages" : "/messages"} 
                      style={{ display: 'block', padding: '12px 16px', color: '#333', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                      onClick={() => setShowUserMenu(false)}
                    >
                      Messages
                    </NavLink>
                    <button 
                      onClick={handleLogout}
                      style={{ 
                        display: 'block', 
                        width: '100%', 
                        padding: '12px 16px', 
                        color: '#e74c3c', 
                        textAlign: 'left',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="action-item" onClick={openLoginModal} style={{ cursor: 'pointer' }}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <path d="M18 3.37109C20.2173 3.37109 22.3741 4.09847 24.1377 5.44238C25.9013 6.78629 27.1745 8.67272 27.7627 10.8105C28.3507 12.9481 28.2213 15.2197 27.3936 17.2764C26.5656 19.3333 25.0849 21.0621 23.1797 22.1963C27.0314 23.4549 30.3319 26.1099 32.4736 29.8125C32.5521 29.9405 32.6044 30.083 32.627 30.2314C32.6495 30.3798 32.6423 30.5313 32.6055 30.6768C32.5686 30.8222 32.5026 30.9594 32.4121 31.0791C32.3215 31.1989 32.2072 31.2999 32.0771 31.375C31.9471 31.4501 31.8033 31.4979 31.6543 31.5166C31.5052 31.5353 31.3536 31.5242 31.209 31.4834C31.0645 31.4426 30.9298 31.373 30.8125 31.2793C30.6952 31.1856 30.598 31.0694 30.5264 30.9375C27.877 26.3587 23.1933 23.625 18 23.625C12.8069 23.6252 8.12391 26.3589 5.47461 30.9375C5.40295 31.0694 5.30574 31.1856 5.18848 31.2793C5.07121 31.373 4.93644 31.4426 4.79199 31.4834C4.64741 31.5242 4.49573 31.5353 4.34668 31.5166C4.19771 31.498 4.05385 31.4501 3.92383 31.375C3.79374 31.2999 3.67951 31.1989 3.58887 31.0791C3.49835 30.9594 3.43241 30.8222 3.39551 30.6768C3.35868 30.5313 3.35147 30.3798 3.37402 30.2314C3.3966 30.083 3.44883 29.9405 3.52734 29.8125C5.66906 26.1084 8.96957 23.4535 12.8213 22.1963C10.9161 21.0621 9.43536 19.3333 8.60742 17.2764C7.7797 15.2197 7.65023 12.9481 8.23828 10.8105C8.82645 8.6727 10.0997 6.78629 11.8633 5.44238C13.6267 4.09857 15.7829 3.37119 18 3.37109ZM19.5371 5.77637C18.0095 5.47251 16.4253 5.62857 14.9863 6.22461C13.5476 6.82068 12.3174 7.83011 11.4521 9.125C10.587 10.42 10.125 11.9426 10.125 13.5C10.1273 15.5879 10.9582 17.5901 12.4346 19.0664C13.9108 20.5425 15.9124 21.3727 18 21.375C19.5575 21.375 21.0809 20.9132 22.376 20.0479C23.6709 19.1825 24.6804 17.9526 25.2764 16.5137C25.8723 15.0748 26.0284 13.4913 25.7246 11.9639C25.4208 10.4364 24.6705 9.03293 23.5693 7.93164C22.4681 6.83038 21.0646 6.08027 19.5371 5.77637Z" fill="#333333"/>
                  <path d="M18 3.37109C20.2173 3.37109 22.3741 4.09847 24.1377 5.44238C25.9013 6.78629 27.1745 8.67272 27.7627 10.8105C28.3507 12.9481 28.2213 15.2197 27.3936 17.2764C26.5656 19.3333 25.0849 21.0621 23.1797 22.1963C27.0314 23.4549 30.3319 26.1099 32.4736 29.8125C32.5521 29.9405 32.6044 30.083 32.627 30.2314C32.6495 30.3798 32.6423 30.5313 32.6055 30.6768C32.5686 30.8222 32.5026 30.9594 32.4121 31.0791C32.3215 31.1989 32.2072 31.2999 32.0771 31.375C31.9471 31.4501 31.8033 31.4979 31.6543 31.5166C31.5052 31.5353 31.3536 31.5242 31.209 31.4834C31.0645 31.4426 30.9298 31.373 30.8125 31.2793C30.6952 31.1856 30.598 31.0694 30.5264 30.9375C27.877 26.3587 23.1933 23.625 18 23.625C12.8069 23.6252 8.12391 26.3589 5.47461 30.9375C5.40295 31.0694 5.30574 31.1856 5.18848 31.2793C5.07121 31.373 4.93644 31.4426 4.79199 31.4834C4.64741 31.5242 4.49573 31.5353 4.34668 31.5166C4.19771 31.498 4.05385 31.4501 3.92383 31.375C3.79374 31.2999 3.67951 31.1989 3.58887 31.0791C3.49835 30.9594 3.43241 30.8222 3.39551 30.6768C3.35868 30.5313 3.35147 30.3798 3.37402 30.2314C3.3966 30.083 3.44883 29.9405 3.52734 29.8125C5.66906 26.1084 8.96957 23.4535 12.8213 22.1963C10.9161 21.0621 9.43536 19.3333 8.60742 17.2764C7.7797 15.2197 7.65023 12.9481 8.23828 10.8105C8.82645 8.6727 10.0997 6.78629 11.8633 5.44238C13.6267 4.09857 15.7829 3.37119 18 3.37109ZM19.5371 5.77637C18.0095 5.47251 16.4253 5.62857 14.9863 6.22461C13.5476 6.82068 12.3174 7.83011 11.4521 9.125C10.587 10.42 10.125 11.9426 10.125 13.5C10.1273 15.5879 10.9582 17.5901 12.4346 19.0664C13.9108 20.5425 15.9124 21.3727 18 21.375C19.5575 21.375 21.0809 20.9132 22.376 20.0479C23.6709 19.1825 24.6804 17.9526 25.2764 16.5137C25.8723 15.0748 26.0284 13.4913 25.7246 11.9639C25.4208 10.4364 24.6705 9.03293 23.5693 7.93164C22.4681 6.83038 21.0646 6.08027 19.5371 5.77637Z" stroke="#333333"/>
                </svg>
                <span>Sign in/<br/>Register</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <nav className={`navbar desktop-only ${isHomePage ? 'homepage-navbar' : ''}`}>
        <div className={`navbar-content ${isHomePage ? 'homepage-grid-layout' : ''}`}>
          {/* Category dropdown - works on all pages */}
          <div 
            className="category-dropdown" 
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
          >
            <svg className="category-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3H4C3.73478 3 3.48043 3.10536 3.29289 3.29289C3.10536 3.48043 3 3.73478 3 4V10C3 10.2652 3.10536 10.5196 3.29289 10.7071C3.48043 10.8946 3.73478 11 4 11H10C10.2652 11 10.5196 10.8946 10.7071 10.7071C10.8946 10.5196 11 10.2652 11 10V4C11 3.73478 10.8946 3.48043 10.7071 3.29289C10.5196 3.10536 10.2652 3 10 3ZM20 3H14C13.7348 3 13.4804 3.10536 13.2929 3.29289C13.1054 3.48043 13 3.73478 13 4V10C13 10.2652 13.1054 10.5196 13.2929 10.7071C13.4804 10.8946 13.7348 11 14 11H20C20.2652 11 20.5196 10.8946 20.7071 10.7071C20.8946 10.5196 21 10.2652 21 10V4C21 3.73478 20.8946 3.48043 20.7071 3.29289C20.5196 3.10536 20.2652 3 20 3ZM10 13H4C3.73478 13 3.48043 13.1054 3.29289 13.2929C3.10536 13.4804 3 13.7348 3 14V20C3 20.2652 3.10536 20.5196 3.29289 20.7071C3.48043 20.8946 3.73478 21 4 21H10C10.2652 21 10.5196 20.8946 10.7071 20.7071C10.8946 20.5196 11 20.2652 11 20V14C11 13.7348 10.8946 13.4804 10.7071 13.2929C10.5196 13.1054 10.2652 13 10 13ZM17 13C17.7826 13 18.548 13.2296 19.2014 13.6603C19.8549 14.091 20.3676 14.7039 20.6761 15.4232C20.9846 16.1424 21.0753 16.9363 20.9371 17.7066C20.7988 18.4769 20.4376 19.1898 19.8983 19.7568C19.3589 20.3239 18.665 20.7202 17.9026 20.8968C17.1402 21.0734 16.3427 21.0225 15.6089 20.7503C14.8752 20.4782 14.2374 19.9967 13.7745 19.3657C13.3117 18.7346 13.0441 17.9816 13.005 17.2L13 17L13.005 16.8C13.0563 15.775 13.4996 14.809 14.2432 14.1017C14.9868 13.3944 15.9738 13 17 13Z" fill="#1F396C"/>
            </svg>
            <span className="category-text">Category</span>
            <svg className={`dropdown-arrow ${showCategoryDropdown ? 'rotated' : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 9.5L12 15.5L6 9.5H18Z" fill="#1F396C" stroke="#1F396C" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            
            {/* Category Dropdown Menu - Builder.io Design */}
            {showCategoryDropdown && (
              <div className="category-menu-wrapper" onClick={(e) => e.stopPropagation()}>
                <div className="category-menu figma-style">
                  <div className="category-menu-list">
                    {categories.map((category, index) => (
                      <div 
                        key={category.id || category.name} 
                        className={`category-menu-item ${hoveredCategory?.id === category.id ? 'active' : ''} ${index === 0 ? 'first-item' : ''}`}
                        onMouseEnter={() => setHoveredCategory(category)}
                        onClick={() => handleCategoryClick(category)}
                      >
                        <span className="category-menu-name">{category.name}</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M8 14.5L12 10.5L8 6.5" stroke={index === 0 || hoveredCategory?.id === category.id ? '#333333' : '#6D6D6D'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    ))}
                  </div>
                  <div className="category-menu-footer">
                    <NavLink to="/all-categories" className="see-more-link" onClick={() => setShowCategoryDropdown(false)}>
                      See more
                    </NavLink>
                  </div>
                </div>
                
                {/* Subcategory Panel - Same style as category menu */}
                {hoveredCategory && hoveredCategory.subcategories && hoveredCategory.subcategories.length > 0 && (
                  <div className="subcategory-panel figma-style">
                    <div className="subcategory-header">
                      <h4>{hoveredCategory.name}</h4>
                    </div>
                    <div className="subcategory-list">
                      {hoveredCategory.subcategories.map((sub, index) => (
                        <div 
                          key={sub.id || index} 
                          className="subcategory-item"
                          onClick={() => handleSubcategoryClick(hoveredCategory, sub)}
                        >
                          <span>{sub.name || sub}</span>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M8 14.5L12 10.5L8 6.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      ))}
                    </div>
                    <div className="subcategory-footer">
                      <span 
                        className="view-all-link"
                        onClick={() => handleCategoryClick(hoveredCategory)}
                      >
                        View all in {hoveredCategory.name} →
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="nav-links">
            <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} end>Home</NavLink>
            {/* TEMPORARILY HIDDEN - Buyer Directory, Buy Leads, Projects, Specialists
            <div className="nav-divider"></div>
            <NavLink to="/buyer-directory" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Buyer Directory</NavLink>
            <div className="nav-divider"></div>
            <NavLink to="/buy-leads" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Buy Leads</NavLink>
            <div className="nav-divider"></div>
            <NavLink to="/projects" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Projects</NavLink>
            <div className="nav-divider"></div>
            <NavLink to="/specialists" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>Specialists</NavLink>
            END TEMPORARILY HIDDEN */}
            <div className="nav-divider"></div>
            <NavLink to="/news" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>News</NavLink>
          </div>
        </div>
        
        {/* Homepage Hero Row - Directly below navbar, shares same container for perfect alignment */}
        {isHomePage && (
          <div className="homepage-hero-row">
            {/* Category Sidebar - Inline version for homepage */}
            <div className="header-category-sidebar">
              <div className="category-list">
                {categories.slice(0, 8).map((category, index) => (
                  <div 
                    key={category.id || category.name} 
                    className="category-item"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <span className="category-name">{category.name}</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M8 14.5L12 10.5L8 6.5" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ))}
              </div>
              <NavLink to="/all-categories" className="see-all-link">See all categories</NavLink>
            </div>
            
            {/* Placeholder for banner - actual banner in HomePage */}
            <div className="header-hero-placeholder"></div>
            
            {/* Placeholder for CTA - actual CTA in HomePage */}
            <div className="header-cta-placeholder"></div>
          </div>
        )}
      </nav>

      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => {
          closeModals();
          setLoginRedirectPath(null);
        }}
        onRegisterClick={openRegisterModal}
        redirectTo={loginRedirectPath}
      />

      {/* Register Modal */}
      <RegisterModal 
        isOpen={isRegisterModalOpen} 
        onClose={closeModals}
        onLoginClick={openLoginModal}
        onVerifyEmail={handleVerifyEmail}
      />

      {/* Verify Email Modal */}
      <VerifyEmailModal
        isOpen={isVerifyEmailModalOpen}
        onClose={closeModals}
        onBack={openRegisterModal}
        onContinue={handleVerifyEmailContinue}
      />

      {/* Requirement Modal (RFQ) */}
      <RequirementModal
        isOpen={isRFQModalOpen}
        onClose={closeRFQModal}
      />
    </header>
  );
};

export default Header;
